/**
 * Create a deal directly.
 *
 * Most deals arrive through lead conversion, which also creates the organization
 * and contact in one transaction. This is for the case where the business starts
 * from an existing customer and there was never a lead.
 */

import { Form, redirect, useActionData, useLoaderData, useNavigation } from 'react-router-dom'
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router-dom'
import { createDealInput } from '@erp/contracts'
import type { Contact, Organization } from '@erp/contracts'
import { api, ApiError } from '../api'
import { invalid, issuesFrom, type FormResult } from '../forms'
import { DealFields, readDealForm } from '../components/deal-form'
import { Button, PageHeader } from '../ui'

interface Data {
  organizations: Organization[]
  contacts: Contact[]
  presetOrganizationId: string | null
}

/** `?organization=<id>` pre-fills the picker, for "new deal" from an org screen. */
export async function loader({ request }: LoaderFunctionArgs): Promise<Data> {
  const url = new URL(request.url)
  const [organizations, contacts] = await Promise.all([
    api.organizations.list({ limit: 200, sort: 'name', direction: 'asc' }),
    api.contacts.list({ limit: 200, sort: 'lastName', direction: 'asc' }),
  ])
  return {
    organizations: organizations.rows,
    contacts: contacts.rows,
    presetOrganizationId: url.searchParams.get('organization'),
  }
}

export async function action({ request }: ActionFunctionArgs): Promise<FormResult | Response> {
  const parsed = createDealInput.safeParse(readDealForm(await request.formData()))
  if (!parsed.success) return invalid(issuesFrom(parsed.error))

  try {
    const deal = await api.deals.create(parsed.data)
    return redirect(`/crm/deals/${deal.id}`)
  } catch (error) {
    if (error instanceof ApiError) return invalid(error.issues, error.message)
    throw error
  }
}

export default function NewDeal() {
  const { organizations, contacts, presetOrganizationId } = useLoaderData() as Data
  const result = useActionData() as FormResult | undefined
  const navigation = useNavigation()
  const saving = navigation.state === 'submitting'

  return (
    <>
      <PageHeader title="New deal" />
      {result?.message ? <div className="ax-alert ax-alert-danger">{result.message}</div> : null}

      <Form method="post" className="ax-form">
        <DealFields
          organizations={organizations}
          contacts={contacts}
          presetOrganizationId={presetOrganizationId}
          issues={result?.issues ?? {}}
        />
        <div className="ax-form-actions">
          <Button variant="solid" type="submit" disabled={saving}>
            {saving ? 'Creating…' : 'Create deal'}
          </Button>
          <a className="ax-btn" href="/crm/deals">
            Cancel
          </a>
        </div>
      </Form>
    </>
  )
}
